"use client";

import { differenceInYears } from "date-fns";
import {
  BASE_COVERAGES,
  isCoverageOn,
  BaseCoverageDef,
  BaseCoverageId,
  baseSumInsuredMax,
  DISEASE_AND_ACCIDENT_LINE_IDS,
  getBaseCoverage,
  primaryLineId,
  subSumInsuredMax,
  WAIVER_HEREDITARY_LIMITS_KEY,
} from "@/config/coverages";
import { ContractCoverages, Coverages, DiseaseAndAccident, OfferResponse } from "@/types/simpego-api";

/** Age-banded coverages (disease, preexistingConditions) switch from the "up to 9 years" to
 * the "over 9 years" deductible percentage at this age. */
export const AGE_BAND_THRESHOLD_YEARS = 9;

/** Whether the pet is currently in the "up to 9 years" deductible band. Unknown birthdate
 * defaults to young, matching the age band that applies to a newly-started policy. */
export const isPetYoungerThanAgeBand = (
  birthDate: Date | string | null | undefined,
  asOf: Date = new Date()
): boolean => {
  if (!birthDate) return true;
  const date = typeof birthDate === "string" ? new Date(birthDate) : birthDate;
  if (Number.isNaN(date.getTime())) return true;
  return differenceInYears(asOf, date) < AGE_BAND_THRESHOLD_YEARS;
};

export interface CoverageState {
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  /** Per-sub-coverage sumInsured (openapi's `SubTreatment.sumInsured`, required whenever
   * `requested` is true), captured from whatever the backend last recommended for it. There's
   * no UI to edit these individually — they're carried through as-is once a recommendation
   * is applied, alongside the top-level Versicherungssumme the customer *can* edit. */
  subCoverageSumInsured: Partial<Record<BaseCoverageId, Record<string, number>>>;
  /** For an "ageBanded" coverage (disease, preexistingConditions), this is whichever band
   * currently applies to the pet's age — `deductiblePercentageUpTo9Yrs` while younger than 9,
   * `deductiblePercentageOver9Yrs` from 9 onward. This is the only band the UI ever shows or
   * edits. For "single" coverages (accident) it's just `deductiblePercentage`. */
  coverageDeductibles: Partial<Record<BaseCoverageId, number>>;
  /** The *other* age band's percentage for an "ageBanded" coverage — never shown/edited, just
   * carried through unchanged from the last recommendation/premium-calculation so it isn't
   * silently overwritten with `coverageDeductibles`' value when the two bands genuinely differ
   * (see buildCoveragesPayload/extractCoverageStateFromOffer). Optional since most call sites
   * only ever read/write the editable band and can leave this to `buildCoveragesPayload`'s
   * fallback. */
  coverageDeductiblesOtherBand?: Partial<Record<BaseCoverageId, number>>;
  /** Base-coverage-level sumInsured, independently editable per coverage — disease and
   * accident each have their own ceiling (openapi: `sumInsured` on each), as does Rescue
   * (openapi: integer 1–10'000). */
  coverageSumInsured: Partial<Record<BaseCoverageId, number>>;
  /** Disease/accident's `sumInsuredPerCase` (openapi: string enum) — not user-editable, but
   * carried through as whatever the backend last recommended for it rather than a hardcoded
   * literal, since the live backend may return different values per bundle/coverage. */
  coverageSumInsuredPerCase: Partial<Record<BaseCoverageId, string>>;
  /** EmergencyCare's per-day sum insured (openapi enum "50"|"100"). `numberDays` isn't
   * user-editable — the API only ever accepts "20", so it's sent as a fixed default. */
  emergencyCareSumInsuredPerDay: "50" | "100" | null;
  /** Krankheit und Unfall's Jahresfranchise in CHF (`diseaseAndAccident.annualDeductible`);
   * null until a recommendation (or the customer) has set it, then `DEFAULT_ANNUAL_DEDUCTIBLE`. */
  annualDeductible?: number | null;
}

// Shared with the step-3 inline field validation (ProductCustomizationOptions.tsx) so the
// live premium query and the on-screen error messages always agree on what counts as valid.
export const SUM_INSURED_MIN = 1000;
export const DEDUCTIBLE_PERCENTAGE_MIN = 0;
// Backend validation for Pet V2: 0–80 (Pet::Coverageable::Disease, ::PreexistingCondition).
export const DEDUCTIBLE_PERCENTAGE_MAX = 80;
export const SUB_SUM_INSURED_MIN = 1;

export const EMPTY_COVERAGE_STATE: CoverageState = {
  coverageSelections: {},
  subCoverageSelections: {},
  subCoverageSumInsured: {},
  coverageDeductibles: {},
  coverageDeductiblesOtherBand: {},
  coverageSumInsured: {},
  coverageSumInsuredPerCase: {},
  emergencyCareSumInsuredPerDay: null,
  annualDeductible: null,
};

export const EMERGENCY_CARE_NUMBER_DAYS = "20" as const;
const EMERGENCY_CARE_DEFAULT_SUM_PER_DAY = "100" as const;
const RESCUE_DEFAULT_SUM_INSURED = 10000;
// Only used before any recommendation has ever returned a `sumInsuredPerCase` for the
// coverage (e.g. the very first request of the funnel) — once the backend has recommended one,
// that value is carried forward instead.
export const DEFAULT_SUM_INSURED_PER_CASE = "25000";
/** `diseaseAndAccident.annualDeductible` is required by the API; every bundle is configured with
 * 0 in the backend's `pet_config.yml` (v2), so that's what is sent until a recommendation or
 * the customer sets another value. Backend validation: 0–10'000. */
export const DEFAULT_ANNUAL_DEDUCTIBLE = 0;
export const ANNUAL_DEDUCTIBLE_MAX = 10000;

const MERGED_FEEDBACK_FIELDS = [
  "underwriting",
  "underwritingReason",
  "underwritingReasonCode",
  "feedbackReason",
  "feedbackReasonCode",
  "netPremiumProRata",
  "insuranceTaxProRata",
] as const;

/** Reads one UI coverage out of an offer/recommendation response. Everything but Krankheit and
 * Unfall is a straight lookup; those two are views onto the single `diseaseAndAccident`
 * coverage: `requested` only when their own flag is set, sub-treatments exposed under the UI's
 * sub ids (so disease's `dentalTreatmentDisease` reads as `dentalTreatment`), and premium /
 * underwriting feedback attached to the primary line only (see `primaryLineId`) so the merged
 * coverage's price isn't shown — or summed — twice. */
export const getOfferCoverage = (
  offer: OfferResponse | null | undefined,
  def: BaseCoverageDef
): Record<string, any> | undefined => {
  if (def.level === "contract") return (offer?.product?.contractCoverages as Record<string, any> | undefined)?.[def.id];
  const coverages = offer?.product?.pets?.[0]?.coverages as Record<string, any> | undefined;
  if (def.id !== "disease" && def.id !== "accident") return coverages?.[def.id];

  const merged = coverages?.diseaseAndAccident as DiseaseAndAccident | undefined;
  if (!merged) return undefined;
  const lineRequested = def.id === "disease" ? merged.diseaseRequested : merged.accidentRequested;
  const view: Record<string, any> = { ...merged, requested: !!merged.requested && !!lineRequested };
  for (const sub of def.subs) view[sub.id] = (merged as Record<string, any>)[sub.apiKey ?? sub.id];
  if (def.id === "accident" && merged.diseaseRequested) {
    for (const field of MERGED_FEEDBACK_FIELDS) delete view[field];
  }
  return view;
};

/** Builds the merged `diseaseAndAccident` request entry from the per-line UI state. Figures
 * shared between the two lines are read from the primary line, falling back to the other one
 * (e.g. a figure that was only ever recommended for Unfall while Krankheit is selected). */
const buildDiseaseAndAccident = (state: CoverageState, isYoung: boolean): DiseaseAndAccident | undefined => {
  const primary = primaryLineId(state.coverageSelections);
  if (!primary) return undefined;
  const other = DISEASE_AND_ACCIDENT_LINE_IDS.find((id) => id !== primary)!;
  const pick = <T>(map: Partial<Record<BaseCoverageId, T>> | undefined): T | undefined => map?.[primary] ?? map?.[other];
  const diseaseSelected = !!state.coverageSelections.disease;

  const editableBand = pick(state.coverageDeductibles);
  const otherBand = pick(state.coverageDeductiblesOtherBand);
  const entry: DiseaseAndAccident = {
    requested: true,
    diseaseRequested: diseaseSelected,
    accidentRequested: !!state.coverageSelections.accident,
    annualDeductible: state.annualDeductible ?? DEFAULT_ANNUAL_DEDUCTIBLE,
    // Required by the live backend's /offers validation even though the openapi spec doesn't
    // mark it required — carried forward from the last recommendation (see CoverageState).
    sumInsuredPerCase: pick(state.coverageSumInsuredPerCase) ?? DEFAULT_SUM_INSURED_PER_CASE,
  };
  // Volldeckung bei Erbkrankheiten/Geburtsgebrechen (waiverHereditaryLimits): Earnix recommends it and the value is carried
  // over from the recommendation (see extractCoverageStateFromOffer). The backend accepts it as "requested" only
  // together with Krankheit, so it is true by default there, and without Krankheit only the recommended
  // (not requested) value is sent. Before any recommendation it is left out.
  const waiver = state.subCoverageSelections[primary]?.[WAIVER_HEREDITARY_LIMITS_KEY] ?? state.subCoverageSelections[other]?.[WAIVER_HEREDITARY_LIMITS_KEY];
  if (diseaseSelected) entry.waiverHereditaryLimits = { requested: waiver ?? true };
  else if (waiver !== undefined) entry.waiverHereditaryLimits = { requested: false };
  // Same age-band handling as the other ageBanded coverages: the UI edits one band, the other
  // is carried through (see `coverageDeductiblesOtherBand`).
  const upTo9Yrs = isYoung ? editableBand : (otherBand ?? editableBand);
  const over9Yrs = isYoung ? (otherBand ?? editableBand) : editableBand;
  if (upTo9Yrs !== undefined) entry.deductiblePercentageUpTo9Yrs = upTo9Yrs;
  if (over9Yrs !== undefined) entry.deductiblePercentageOver9Yrs = over9Yrs;
  const sum = pick(state.coverageSumInsured);
  if (sum != null) entry.sumInsured = sum;

  const record = entry as unknown as Record<string, unknown>;
  for (const sub of getBaseCoverage("disease").subs) {
    // Dental and preventive treatment exist for the Krankheit line only (SIP-4660, claims check).
    const requested = (sub.diseaseOnly ? diseaseSelected : true) &&
      !!(state.subCoverageSelections[primary]?.[sub.id] ?? state.subCoverageSelections[other]?.[sub.id]);
    const subSum = state.subCoverageSumInsured[primary]?.[sub.id] ?? state.subCoverageSumInsured[other]?.[sub.id];
    record[sub.apiKey ?? sub.id] = requested && subSum != null ? { requested, sumInsured: subSum } : { requested };
  }
  return entry;
};

/**
 * Builds the `coverages` object for a /recommendations, /premium-calculations, or /offers
 * request by iterating the coverage config instead of hand-writing each field. Only
 * `requested`-flag data is included unless a deductible/sumInsured override has been set
 * (e.g. after the user visited the step-3 coverage editor) — otherwise the backend
 * recommends/computes those figures itself.
 *
 * `petBirthDate` decides, for "ageBanded" coverages, which of the two API deductible fields
 * `coverageDeductibles[id]` (the one the UI shows/edits) maps to — the other band comes from
 * `coverageDeductiblesOtherBand` untouched. Previously both fields were always set to the same
 * single number, which silently overwrote whichever band the customer wasn't editing whenever
 * the backend had recommended two genuinely different percentages (e.g. preexistingConditions
 * often gets a higher over-9-years percentage than up-to-9-years), producing a real price jump
 * the moment step 3 re-requested the premium.
 */
export const buildCoveragesPayload = (state: CoverageState, petBirthDate: Date | string | null): Coverages => {
  const coverages: Coverages = {};
  const isYoung = isPetYoungerThanAgeBand(petBirthDate);

  const diseaseAndAccident = buildDiseaseAndAccident(state, isYoung);
  if (diseaseAndAccident) coverages.diseaseAndAccident = diseaseAndAccident;

  for (const def of BASE_COVERAGES) {
    if (def.level !== "pet") continue; // contract-level coverages go through buildContractCoveragesPayload
    // Krankheit/Unfall are sent together as `diseaseAndAccident` (built above).
    if (DISEASE_AND_ACCIDENT_LINE_IDS.includes(def.id)) continue;
    // Never offered (legal): sent explicitly as not requested.
    if (def.neverRequested) {
      (coverages as Record<string, unknown>)[def.id] = { requested: false };
      continue;
    }
    // alwaysRequested coverages (rescue) have no checkbox on step 1 — always sent as
    // requested regardless of `coverageSelections`, except rescue, which can be switched off on step 3.
    if (!isCoverageOn(state.coverageSelections, def)) continue;
    if (def.requires && !state.coverageSelections[def.requires]) continue; // gating safety net

    const entry: Record<string, unknown> = { requested: true };

    if (def.deductible === "ageBanded") {
      const editableBand = state.coverageDeductibles[def.id];
      const otherBand = state.coverageDeductiblesOtherBand?.[def.id];
      // The backend requires both age-band fields whenever the coverage is requested, even
      // though the UI only ever shows/edits whichever band currently applies to the pet's age.
      // Prefer a genuinely different recommended value for the other band when one is known
      // (see the doc comment above); otherwise fall back to the editable band's value instead
      // of omitting the field, which the backend rejects (e.g. a coverage added after the
      // pet's initial pricing, where no recommendation ever set the other band).
      const upTo9Yrs = isYoung ? editableBand : (otherBand ?? editableBand);
      const over9Yrs = isYoung ? (otherBand ?? editableBand) : editableBand;
      if (upTo9Yrs !== undefined) entry.deductiblePercentageUpTo9Yrs = upTo9Yrs;
      if (over9Yrs !== undefined) entry.deductiblePercentageOver9Yrs = over9Yrs;
    } else if (def.deductible === "single") {
      const pct = state.coverageDeductibles[def.id];
      if (pct !== undefined) entry.deductiblePercentage = pct;
    }

    // PreexistingConditions has its own sumInsured (openapi: integer), but no sumInsuredPerCase
    // field (that's only on DiseaseAndAccident).
    if (def.id === "preexistingConditions") {
      const sum = state.coverageSumInsured[def.id];
      if (sum != null) entry.sumInsured = sum;
    }

    // Rescue's own sumInsured (openapi: integer 1–10'000) — customer-editable on step 3;
    // falls back to the max so a first-time selection is still a valid request.
    if (def.id === "rescue") {
      entry.sumInsured = state.coverageSumInsured[def.id] ?? RESCUE_DEFAULT_SUM_INSURED;
    }

    for (const sub of def.subs) {
      // Even "fixed" (non-togglable-in-UI) subs like medicalFood are sourced from whatever
      // the backend actually recommended, not hardcoded — the backend independently decides
      // per-sub eligibility (e.g. it may decline medicalFood for a given pet/bundle), and
      // forcing `requested: true` here without a matching sumInsured would both misrepresent
      // the recommendation and violate the API's "sumInsured required if requested" rule.
      const requested = !!state.subCoverageSelections[def.id]?.[sub.id];
      const subSum = state.subCoverageSumInsured[def.id]?.[sub.id];
      // sumInsured is required by the API whenever requested is true — omit it only when we
      // genuinely don't have a known figure yet (e.g. no recommendation applied so far).
      entry[sub.id] = !def.subsAreFlags && requested && subSum != null ? { requested, sumInsured: subSum } : { requested };
    }

    (coverages as Record<string, unknown>)[def.id] = entry;
  }

  return coverages;
};

/** Builds the `product.contractCoverages` object (propertyDamage/emergencyCare) — both are
 * `alwaysRequested` (Pet V2 has no checkbox for either), so they're always included with
 * `requested: true` regardless of `coverageSelections`. Mirrors `buildCoveragesPayload` but for
 * the contract-level coverages, which are requested outside of `pets[].coverages`. */
export const buildContractCoveragesPayload = (state: CoverageState): ContractCoverages | undefined => {
  const contractCoverages: ContractCoverages = {};

  if (isCoverageOn(state.coverageSelections, getBaseCoverage("emergencyCare"))) {
    contractCoverages.emergencyCare = {
      requested: true,
      sumInsuredPerDay: state.emergencyCareSumInsuredPerDay ?? EMERGENCY_CARE_DEFAULT_SUM_PER_DAY,
      numberDays: EMERGENCY_CARE_NUMBER_DAYS,
    };
  }

  return contractCoverages;
};

/** Hydrates editable coverage state from a live offer/recommendation response (e.g. when
 * re-opening an existing offer, or seeding step 3 from the tier the customer picked). */
export const extractCoverageStateFromOffer = (offer: OfferResponse | null | undefined): CoverageState => {
  const pet = offer?.product?.pets?.[0];
  const contractCoverages = offer?.product?.contractCoverages;
  if (!pet?.coverages && !contractCoverages) return { ...EMPTY_COVERAGE_STATE };

  const isYoung = isPetYoungerThanAgeBand(pet?.birthdate ?? null);

  const coverageSelections: Partial<Record<BaseCoverageId, boolean>> = {};
  const subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>> = {};
  const subCoverageSumInsured: Partial<Record<BaseCoverageId, Record<string, number>>> = {};
  const coverageDeductibles: Partial<Record<BaseCoverageId, number>> = {};
  const coverageDeductiblesOtherBand: Partial<Record<BaseCoverageId, number>> = {};
  const coverageSumInsured: Partial<Record<BaseCoverageId, number>> = {};
  const coverageSumInsuredPerCase: Partial<Record<BaseCoverageId, string>> = {};
  let emergencyCareSumInsuredPerDay: "50" | "100" | null = null;
  let annualDeductible: number | null = null;

  for (const def of BASE_COVERAGES) {
    const cov = getOfferCoverage(offer, def);
    if (def.deselectable && pet?.coverages && !cov?.requested) coverageSelections[def.id] = false;
    if (!cov?.requested || def.neverRequested) continue;
    // An always-requested coverage (legal, …) the underwriting rejected is not part of the
    // contract, so it must not come back as selected (and show up in summaries).
    if (def.alwaysRequested && cov.underwriting === false) continue;
    coverageSelections[def.id] = true;

    if (def.deductible === "ageBanded") {
      // Keep the two bands separate instead of collapsing them into one number — see
      // `coverageDeductiblesOtherBand`'s doc comment for why that used to cause price jumps.
      const upTo9Yrs = cov.deductiblePercentageUpTo9Yrs;
      const over9Yrs = cov.deductiblePercentageOver9Yrs;
      const editableBand = isYoung ? upTo9Yrs : over9Yrs;
      const otherBand = isYoung ? over9Yrs : upTo9Yrs;
      if (typeof editableBand === "number") coverageDeductibles[def.id] = editableBand;
      if (typeof otherBand === "number") coverageDeductiblesOtherBand[def.id] = otherBand;
    } else if (def.deductible === "single") {
      if (typeof cov.deductiblePercentage === "number") coverageDeductibles[def.id] = cov.deductiblePercentage;
    }

    if (
      (def.id === "disease" || def.id === "accident" || def.id === "rescue" || def.id === "preexistingConditions") &&
      typeof cov.sumInsured === "number"
    ) {
      coverageSumInsured[def.id] = cov.sumInsured;
    }

    if ((def.id === "disease" || def.id === "accident") && typeof cov.sumInsuredPerCase === "string") {
      coverageSumInsuredPerCase[def.id] = cov.sumInsuredPerCase;
    }

    if ((def.id === "disease" || def.id === "accident") && typeof cov.annualDeductible === "number") {
      annualDeductible = cov.annualDeductible;
    }

    if (def.id === "emergencyCare" && (cov.sumInsuredPerDay === "50" || cov.sumInsuredPerDay === "100")) {
      emergencyCareSumInsuredPerDay = cov.sumInsuredPerDay;
    }

    const subs: Record<string, boolean> = {};
    const subSums: Record<string, number> = {};
    for (const sub of def.subs) {
      const subCov = cov[sub.id];
      const backendRequested = !!subCov?.requested;
      // Mirror the backend's own decision for every sub, including "fixed" ones — the backend
      // decides sub-level eligibility independently and may decline even a supposedly-always-
      // included sub like medicalFood for a given pet/bundle.
      subs[sub.id] = backendRequested;
      // Only trust a sub's sumInsured when the backend itself requested it — a not-requested
      // sub may carry a 0 or otherwise meaningless figure that would fail the API's
      // `minimum: 1` validation if forwarded as-is.
      if (!def.subsAreFlags && backendRequested && typeof subCov?.sumInsured === "number" && subCov.sumInsured > 0) {
        subSums[sub.id] = subCov.sumInsured;
      }
    }
    // The recommended Volldeckung bei Erbkrankheiten (waiverHereditaryLimits) is not a sub of the config, but is kept
    // next to them so it is sent back with every following request.
    if ((def.id === "disease" || def.id === "accident") && typeof cov.waiverHereditaryLimits?.requested === "boolean") {
      subs[WAIVER_HEREDITARY_LIMITS_KEY] = cov.waiverHereditaryLimits.requested;
    }
    subCoverageSelections[def.id] = subs;
    subCoverageSumInsured[def.id] = subSums;
  }

  return {
    coverageSelections,
    subCoverageSelections,
    subCoverageSumInsured,
    coverageDeductibles,
    coverageDeductiblesOtherBand,
    coverageSumInsured,
    coverageSumInsuredPerCase,
    emergencyCareSumInsuredPerDay,
    annualDeductible,
  };
};

/** True when every currently-selected coverage's required editable fields (base sum insured,
 * deductible %, non-fixed sub sum insured) hold a value within range — mirrors the same
 * per-field checks shown as inline errors on step 3, so the live premium query only fires
 * once the customer's inputs are actually valid instead of on every half-typed or
 * out-of-range figure. */
export const isCoverageStateValid = (state: CoverageState): boolean => {
  if (
    (state.coverageSelections.disease || state.coverageSelections.accident) &&
    state.annualDeductible != null &&
    (state.annualDeductible < 0 || state.annualDeductible > ANNUAL_DEDUCTIBLE_MAX)
  ) {
    return false;
  }

  for (const id of ["disease", "accident", "preexistingConditions"] as const) {
    if (!state.coverageSelections[id]) continue;
    const value = state.coverageSumInsured[id];
    // Vorerkrankung's own sum insured may be 1–10'000 (openapi), the Krankheit-und-Unfall one starts at 1'000.
    const min = id === "preexistingConditions" ? SUB_SUM_INSURED_MIN : SUM_INSURED_MIN;
    if (value == null || value < min || value > baseSumInsuredMax(id)) return false;
  }

  // Rettung & Bergung is always requested; its own sum insured has to stay within 1–10'000 (openapi).
  const rescueSum = state.coverageSumInsured.rescue;
  if (state.coverageSelections.rescue !== false && rescueSum != null && (rescueSum < SUB_SUM_INSURED_MIN || rescueSum > baseSumInsuredMax("rescue"))) return false;

  for (const def of BASE_COVERAGES) {
    if (def.deductible === "none" || !state.coverageSelections[def.id]) continue;
    const value = state.coverageDeductibles[def.id];
    if (value == null || value < DEDUCTIBLE_PERCENTAGE_MIN || value > DEDUCTIBLE_PERCENTAGE_MAX) return false;
  }

  for (const def of BASE_COVERAGES) {
    if (!state.coverageSelections[def.id]) continue;
    if (def.subsAreFlags) continue;
    for (const sub of def.subs) {
      if (sub.fixed) continue;
      if (!state.subCoverageSelections[def.id]?.[sub.id]) continue;
      const value = state.subCoverageSumInsured[def.id]?.[sub.id];
      if (value == null || value < SUB_SUM_INSURED_MIN || value > subSumInsuredMax(sub)) return false;
      // A sub-coverage can never exceed the main coverage's sum insured (same rule as the step-3 form).
      const mainSum = DISEASE_AND_ACCIDENT_LINE_IDS.includes(def.id) ? state.coverageSumInsured[def.id] : undefined;
      if (mainSum != null && value > mainSum) return false;
    }
  }

  return true;
};

/** Reads the per-coverage Teilprämie (gross = net + tax) the backend computed for each
 * requested base coverage on a recommendation/premium-calculation response — used to show a
 * live partial premium next to each coverage checkbox on step 1. */
export const extractCoveragePremiums = (
  offer: OfferResponse | null | undefined
): Partial<Record<BaseCoverageId, number>> => {
  const premiums: Partial<Record<BaseCoverageId, number>> = {};

  for (const def of BASE_COVERAGES) {
    const cov = getOfferCoverage(offer, def);
    // A coverage the backend declined outright (underwriting: false) still comes back with
    // `requested: true` and `netPremiumProRata: 0` — excluded here so it doesn't read as a
    // legitimately-priced "CHF 0.00" coverage; see `extractDeclinedCoverages` for that case.
    if (!cov?.requested || cov?.underwriting === false || typeof cov.netPremiumProRata !== "number") continue;
    const tax = typeof cov.insuranceTaxProRata === "number" ? cov.insuranceTaxProRata : 0;
    premiums[def.id] = cov.netPremiumProRata + tax;
  }

  // The Zahlweise surcharge (`yearlyPaymentFrequency`) belongs to no coverage of its own; it is shown as part
  // of Krankheit und Unfall so that the Teilprämien add up to the price in the cart.
  const frequency = offer?.yearlyPaymentFrequency;
  const surcharge = (frequency?.netPremiumProRata ?? 0) + (frequency?.insuranceTaxProRata ?? 0);
  if (surcharge > 0) {
    for (const id of DISEASE_AND_ACCIDENT_LINE_IDS) {
      if (premiums[id] !== undefined) premiums[id] += surcharge;
    }
  }

  return premiums;
};

/** Base coverages the customer requested that the backend rejected outright (openapi:
 * `underwriting: false` on the coverage itself, e.g. "Prämie Baustein Vorerkrankung zu hoch")
 * — distinct from a single declined sub-treatment, this means the whole coverage isn't being
 * priced/included even though it still comes back as `requested: true`. Keyed by the coverage's
 * own `underwritingReason` so the UI can show why instead of a misleading "CHF 0.00". */
export const extractDeclinedCoverages = (
  offer: OfferResponse | null | undefined
): Partial<Record<BaseCoverageId, string>> => {
  const declined: Partial<Record<BaseCoverageId, string>> = {};

  for (const def of BASE_COVERAGES) {
    const cov = getOfferCoverage(offer, def);
    if (cov?.requested && cov?.underwriting === false) {
      declined[def.id] = typeof cov.underwritingReason === "string" ? cov.underwritingReason : "";
    }
  }

  return declined;
};
